import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiEdit2, FiTrash2, FiPlus, FiLogOut, FiShare2,
  FiLink, FiFolder, FiChevronRight, FiEye,
  FiSettings, FiShield, FiCornerUpLeft, FiCpu, FiSmartphone, FiStar,
  FiExternalLink, FiCopy, FiCheck, FiSquare, FiCheckSquare
} = FiIcons;

const ContextMenu = ({
  x, y, item, onEdit, onDelete, onAddBookmark, onAddFolder,
  onMoveOut, onShare, onLeaveFolder, canMoveOut, onClose, themeColors,
  isDark = false, onSettings, onSignOut, isAdmin, onAdmin,
  canMoveFolderOut, onMoveFolderOut, canAdd,
  onAISummary, onQRCode, onTogglePin, isPinned = false,
  onOpenFolder, onMoveTo, onToggleSelect, isSelected = false,
  onSelectAll, selectedCount = 0, onClearSelection
}) => {
  const menuRef = useRef(null);
  const [addExpanded, setAddExpanded] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  const hoverBg = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';
  const menuBg = isDark ? 'rgba(18,18,22,0.96)' : 'rgba(255,255,255,0.96)';

  useEffect(() => {
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) onClose();
    };
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('touchstart', handleClick, { passive: true });
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('touchstart', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [onClose]);

  const isSharedFolder = item?._isShared && item?.type === 'folder';
  const isSharedItem = item?._isShared;
  const permission = item?._sharedPermission || 'viewer';
  const isEditor = permission === 'editor';
  const isViewer = permission === 'viewer';
  const isSharedRoot = isSharedFolder && !!item?._memberRecordId;

  const canEdit = !!item && !isSharedItem;
  const canDelete = !!item && (!isSharedItem || (isSharedItem && isEditor && item.type !== 'folder'));
  const canMoveOutItem = !!item && item.type === 'link' && canMoveOut && !isSharedItem;
  const canMoveFolderOutItem = !!item && item.type === 'folder' && canMoveFolderOut && (!isSharedItem || isSharedRoot);
  const canShareFolder = !!item && item.type === 'folder' && !isSharedItem;
  const canLeave = isSharedFolder;

  const isScreenClick = !item;
  const showAddNew = isScreenClick && (canAdd !== false);

  const handleCopyLink = (e) => {
    e.stopPropagation();
    if (!item?.url) return;
    try {
      navigator.clipboard.writeText(item.url);
      setCopiedLink(true);
      setTimeout(() => {
        setCopiedLink(false);
        onClose();
      }, 700);
    } catch {
      onClose();
    }
  };

  const handleOpenLink = () => {
    if (!item?.url) return;
    window.open(item.url, '_blank', 'noopener,noreferrer');
    onClose();
  };

  const handleOpenFolder = () => {
    if (onOpenFolder) onOpenFolder(item);
    onClose();
  };

  // Grouped clean actions
  const bookmarkActions = item?.type === 'link' ? [
    {
      id: 'open-tab',
      icon: FiExternalLink,
      label: 'Open in New Tab',
      onClick: handleOpenLink,
      show: true,
      danger: false,
      accent: false,
    },
    {
      id: 'copy-url',
      icon: copiedLink ? FiCheck : FiCopy,
      label: copiedLink ? 'Link Copied!' : 'Copy Link',
      onClick: handleCopyLink,
      show: true,
      danger: false,
      accent: copiedLink,
    },
    {
      id: 'select-bm',
      icon: isSelected ? FiCheckSquare : FiSquare,
      label: isSelected ? 'Deselect Item' : 'Select Item (Multi-select)',
      onClick: () => { if (onToggleSelect) onToggleSelect(item); onClose(); },
      show: !!onToggleSelect,
      danger: false,
      accent: isSelected,
    },
    {
      id: 'move-to-bm',
      icon: FiFolder,
      label: 'Move to Folder...',
      onClick: () => { if (onMoveTo) onMoveTo(item); onClose(); },
      show: canEdit,
      danger: false,
      accent: true,
    },
    {
      id: 'edit-bm',
      icon: FiEdit2,
      label: 'Edit',
      onClick: () => { onEdit(); onClose(); },
      show: canEdit,
      danger: false,
      accent: false,
    },
    {
      id: 'ai-summary',
      icon: FiCpu,
      label: 'AI Summary',
      onClick: () => { if (onAISummary) onAISummary(item); onClose(); },
      show: true,
      danger: false,
      accent: true,
    },
    {
      id: 'qr-code',
      icon: FiSmartphone,
      label: 'Mobile QR Code',
      onClick: () => { if (onQRCode) onQRCode(item); onClose(); },
      show: true,
      danger: false,
      accent: false,
    },
    {
      id: 'pin-dock',
      icon: FiStar,
      label: isPinned ? 'Unpin from Dock' : 'Pin to Dock',
      onClick: () => { if (onTogglePin) onTogglePin(item); onClose(); },
      show: true,
      danger: false,
      accent: false,
    },
    {
      id: 'move-out-bm',
      icon: FiCornerUpLeft,
      label: 'Move Out',
      onClick: () => { onMoveOut(); onClose(); },
      show: canMoveOutItem,
      danger: false,
      accent: false,
    },
    {
      id: 'delete-bm',
      icon: FiTrash2,
      label: 'Delete',
      onClick: () => { onDelete(); onClose(); },
      show: canDelete,
      danger: true,
      accent: false,
      separatorBefore: true,
    },
  ].filter(a => a.show) : [];

  const folderActions = item?.type === 'folder' ? [
    {
      id: 'open-folder',
      icon: FiFolder,
      label: 'Open Folder',
      onClick: handleOpenFolder,
      show: !!onOpenFolder,
      danger: false,
      accent: false,
    },
    {
      id: 'select-fld',
      icon: isSelected ? FiCheckSquare : FiSquare,
      label: isSelected ? 'Deselect Folder' : 'Select Folder (Multi-select)',
      onClick: () => { if (onToggleSelect) onToggleSelect(item); onClose(); },
      show: !!onToggleSelect,
      danger: false,
      accent: isSelected,
    },
    {
      id: 'move-to-fld',
      icon: FiFolder,
      label: 'Move to Folder...',
      onClick: () => { if (onMoveTo) onMoveTo(item); onClose(); },
      show: canEdit,
      danger: false,
      accent: true,
    },
    {
      id: 'edit-folder',
      icon: FiEdit2,
      label: 'Edit Folder',
      onClick: () => { onEdit(); onClose(); },
      show: canEdit,
      danger: false,
      accent: false,
    },
    {
      id: 'share-folder',
      icon: FiShare2,
      label: 'Share Folder',
      onClick: () => { onShare(); onClose(); },
      show: canShareFolder,
      danger: false,
      accent: true,
    },
    {
      id: 'manage-access',
      icon: FiEye,
      label: 'Manage Access',
      onClick: () => { onShare(); onClose(); },
      show: isSharedFolder,
      danger: false,
      accent: true,
    },
    {
      id: 'move-folder-out',
      icon: FiCornerUpLeft,
      label: 'Move Folder Out',
      onClick: () => { if (onMoveFolderOut) { onMoveFolderOut(); onClose(); } },
      show: canMoveFolderOutItem,
      danger: false,
      accent: false,
    },
    {
      id: 'leave-folder',
      icon: FiLogOut,
      label: 'Leave Folder',
      onClick: () => { if (onLeaveFolder) { onLeaveFolder(); onClose(); } },
      show: canLeave,
      danger: true,
      accent: false,
    },
    {
      id: 'delete-folder',
      icon: FiTrash2,
      label: 'Delete Folder',
      onClick: () => { onDelete(); onClose(); },
      show: canDelete,
      danger: true,
      accent: false,
      separatorBefore: true,
    },
  ].filter(a => a.show) : [];

  const actions = item?.type === 'folder' ? folderActions : bookmarkActions;

  const numItems =
    actions.length +
    (showAddNew ? 1 : 0) +
    (isSharedItem && isViewer ? 1 : 0) +
    (isScreenClick ? 2 + (isAdmin ? 1 : 0) : 0);

  const estimatedHeight = (numItems * 34) + (item ? 38 : 0) + 24;
  const menuW = 200;
  const left = Math.max(8, Math.min(x, window.innerWidth - menuW - 8));
  const top = Math.max(8, Math.min(y, window.innerHeight - estimatedHeight - 8));

  const buttonClass =
    "flex items-center gap-2.5 px-3 py-1.5 mx-1 my-0.5 text-xs font-medium transition-colors duration-100 text-left rounded-md";
  const buttonStyle = { width: 'calc(100% - 8px)' };

  return (
    <AnimatePresence>
      <motion.div
        ref={menuRef}
        initial={{ opacity: 0, scale: 0.94, y: -4 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.94, y: -4 }}
        transition={{ duration: 0.12 }}
        onMouseDown={(e) => e.stopPropagation()}
        data-ctxmenu
        className="fixed z-[9999] rounded-xl shadow-2xl py-1 scrollbar-none"
        style={{
          left,
          top,
          width: menuW,
          maxHeight: 'calc(100vh - 16px)',
          overflowY: 'auto',
          backgroundColor: menuBg,
          border: `1px solid ${themeColors.border || 'rgba(255,255,255,0.12)'}`,
          backdropFilter: 'blur(16px)',
        }}
      >
        {item && (
          <div
            className="px-3 py-1.5 mb-1 border-b flex items-center gap-2"
            style={{ borderColor: themeColors.border || 'rgba(255,255,255,0.1)' }}
          >
            <span
              className="text-[10px] font-semibold uppercase tracking-wider truncate flex-1"
              style={{ color: themeColors.muted || '#888' }}
            >
              {item.title}
            </span>
            {isSharedItem && (
              <span
                className="text-[9px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0"
                style={{
                  backgroundColor: isEditor ? 'rgba(52,211,153,0.15)' : 'rgba(167,139,250,0.15)',
                  color: isEditor ? '#34d399' : '#a78bfa',
                  border: `1px solid ${isEditor ? '#34d39944' : '#a78bfa44'}`,
                }}
              >
                {isEditor ? 'Editor' : 'Viewer'}
              </span>
            )}
          </div>
        )}

        {actions.map(({ id, icon, label, onClick, danger, accent, separatorBefore }) => (
          <React.Fragment key={id || label}>
            {separatorBefore && (
              <div
                className="mx-2 my-1 border-t"
                style={{ borderColor: themeColors.border || 'rgba(255,255,255,0.08)' }}
              />
            )}
            <button
              onClick={onClick}
              className={buttonClass}
              style={{
                ...buttonStyle,
                color: danger ? '#ff6b6b' : accent ? (themeColors.accent || '#34d399') : themeColors.text,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = hoverBg; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <SafeIcon icon={icon} className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="truncate">{label}</span>
            </button>
          </React.Fragment>
        ))}

        {showAddNew && (
          <div
            className="relative"
            onMouseEnter={() => setAddExpanded(true)}
            onMouseLeave={() => setAddExpanded(false)}
          >
            <button
              className={`${buttonClass} justify-between`}
              style={{
                ...buttonStyle,
                color: themeColors.text,
                backgroundColor: addExpanded ? hoverBg : 'transparent',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = hoverBg; }}
              onMouseLeave={(e) => { if (!addExpanded) e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <div className="flex items-center gap-2.5">
                <SafeIcon icon={FiPlus} className="w-3.5 h-3.5 flex-shrink-0" />
                Add New
              </div>
              <SafeIcon icon={FiChevronRight} className="w-3 h-3" style={{ color: themeColors.muted }} />
            </button>

            <AnimatePresence>
              {addExpanded && (
                <motion.div
                  initial={{ opacity: 0, x: -6, y: -6 }}
                  animate={{ opacity: 1, x: 0, y: 0 }}
                  exit={{ opacity: 0, x: -6, y: -6 }}
                  transition={{ duration: 0.15 }}
                  className="absolute top-0 w-[140px] rounded-xl shadow-2xl py-1 z-50"
                  style={{
                    left: left + menuW + 140 < window.innerWidth ? '100%' : 'auto',
                    right: left + menuW + 140 >= window.innerWidth ? '100%' : 'auto',
                    marginLeft: left + menuW + 140 < window.innerWidth ? '4px' : '0',
                    marginRight: left + menuW + 140 >= window.innerWidth ? '4px' : '0',
                    backgroundColor: menuBg,
                    border: `1px solid ${themeColors.border || 'rgba(255,255,255,0.12)'}`,
                    backdropFilter: 'blur(16px)',
                  }}
                >
                  <button
                    onClick={() => { onAddBookmark(); onClose(); }}
                    className={buttonClass}
                    style={{ ...buttonStyle, color: themeColors.text }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = hoverBg; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                  >
                    <SafeIcon icon={FiLink} className="w-3.5 h-3.5 flex-shrink-0" style={{ color: themeColors.accent }} />
                    Bookmark
                  </button>
                  <button
                    onClick={() => { onAddFolder(); onClose(); }}
                    className={buttonClass}
                    style={{ ...buttonStyle, color: themeColors.text }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = hoverBg; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                  >
                    <SafeIcon icon={FiFolder} className="w-3.5 h-3.5 flex-shrink-0" style={{ color: themeColors.accent }} />
                    Folder
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {isSharedItem && isViewer && (
          <div className="px-3 py-1.5 text-[10px] mx-1" style={{ color: themeColors.muted }}>
            View only — contact owner to edit
          </div>
        )}

        {isScreenClick && (
          <>
            {onSelectAll && (
              <button
                onClick={() => { onSelectAll(); onClose(); }}
                className={buttonClass}
                style={{ ...buttonStyle, color: themeColors.accent || '#10b981' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = hoverBg; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <SafeIcon icon={FiCheckSquare} className="w-3.5 h-3.5 flex-shrink-0" />
                Select All Items
              </button>
            )}

            {selectedCount > 0 && onClearSelection && (
              <button
                onClick={() => { onClearSelection(); onClose(); }}
                className={buttonClass}
                style={{ ...buttonStyle, color: themeColors.muted }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = hoverBg; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <SafeIcon icon={FiSquare} className="w-3.5 h-3.5 flex-shrink-0" />
                Deselect All ({selectedCount})
              </button>
            )}

            {(onSelectAll || (selectedCount > 0 && onClearSelection)) && (
              <div className="mx-3 my-1 border-t" style={{ borderColor: themeColors.border || 'rgba(255,255,255,0.08)' }} />
            )}

            {showAddNew && (
              <div className="mx-3 my-1 border-t" style={{ borderColor: themeColors.border || 'rgba(255,255,255,0.08)' }} />
            )}

            <button
              onClick={() => { onSettings(); onClose(); }}
              className={buttonClass}
              style={{ ...buttonStyle, color: themeColors.text }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = hoverBg; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <SafeIcon icon={FiSettings} className="w-3.5 h-3.5 flex-shrink-0" style={{ color: themeColors.muted }} />
              Settings
            </button>

            {isAdmin && (
              <button
                onClick={() => { onAdmin(); onClose(); }}
                className={buttonClass}
                style={{ ...buttonStyle, color: themeColors.accent }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = hoverBg; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <SafeIcon icon={FiShield} className="w-3.5 h-3.5 flex-shrink-0" />
                Admin Panel
              </button>
            )}

            <button
              onClick={() => { onSignOut(); onClose(); }}
              className={buttonClass}
              style={{ ...buttonStyle, color: '#ff6b6b' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = hoverBg; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <SafeIcon icon={FiLogOut} className="w-3.5 h-3.5 flex-shrink-0" />
              Sign Out
            </button>
          </>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

export default ContextMenu;